export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE_URL = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';

let bearer: string | null = null;
export function setBearer(token: string | null) { bearer = token; }

/* ───────── Auth/refresh wiring (configurado en el bootstrap) ───────── */

interface AuthConfig {
  refreshPath: string;
  logoutPath?: string;
  onRefreshed: (accessToken: string) => void;
  onSessionExpired: () => void;
}
let authConfig: AuthConfig | null = null;
export function configureAuth(cfg: AuthConfig) { authConfig = cfg; }

/** Cierra la sesión en el server (revoca el refresh + limpia la cookie). Best-effort. */
export async function requestLogout(): Promise<void> {
  if (!authConfig?.logoutPath) return;
  try {
    await fetch(`${BASE_URL}${authConfig.logoutPath}`, { method: 'POST', credentials: 'include' });
  } catch {
    /* best-effort */
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/** Renueva el access token usando la cookie httpOnly de refresh (single-flight). */
async function tryRefresh(): Promise<boolean> {
  if (!authConfig) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const resp = await fetch(`${BASE_URL}${authConfig!.refreshPath}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        });
        if (!resp.ok) return false;
        const data = await resp.json().catch(() => null);
        const token = data?.accessToken;
        if (!token) return false;
        bearer = token;
        authConfig!.onRefreshed(token);
        return true;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function rawRequest(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let resp: Response;
  try {
    resp = await rawRequest(method, path, body);
  } catch (err) {
    throw new ApiError('NETWORK', (err as Error).message ?? 'Network error', 0);
  }

  // 401 → intentar renovar el access token una vez y reintentar el request.
  if (resp.status === 401 && authConfig) {
    const ok = await tryRefresh();
    if (ok) {
      try {
        resp = await rawRequest(method, path, body);
      } catch (err) {
        throw new ApiError('NETWORK', (err as Error).message ?? 'Network error', 0);
      }
    } else {
      authConfig.onSessionExpired();
      throw new ApiError('AUTH_EXPIRED', 'Sesión expirada', 401);
    }
  }

  if (!resp.ok) {
    let payload: any = {};
    try { payload = await resp.json(); } catch { /* */ }
    throw new ApiError(payload?.code ?? 'HTTP_ERROR', payload?.message ?? 'Error', resp.status);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  get:  <T>(p: string)               => request<T>('GET',    p),
  post: <T>(p: string, body?: unknown) => request<T>('POST',   p, body),
  put:  <T>(p: string, body?: unknown) => request<T>('PUT',    p, body),
  patch:<T>(p: string, body?: unknown) => request<T>('PATCH',  p, body),
  del:  <T>(p: string)               => request<T>('DELETE', p),
};

/* ───────── Cliente para SSR ───────── */

export interface ApiClient {
  get<T>(path: string): Promise<T>;
}

export interface CreateApiClientOptions {
  /** URL absoluta de la API (en el server no hay origin relativo). */
  baseUrl: string;
  /** Headers a reenviar. `host` es clave: con él la API resuelve el tenant. */
  headers?: Record<string, string>;
  fetchImpl?: typeof fetch;
}

/**
 * Cliente HTTP para usar **desde los loaders** (Node).
 *
 * El `api` de arriba está pensado para el browser: URL relativa al origin, cookies y
 * refresh de sesión. En SSR no hay nada de eso — hay que pasar URL absoluta y reenviar el
 * `host` del request entrante para que la API resuelva el tenant por dominio. Solo lectura
 * pública: el sitio no autentica.
 */
export function createApiClient(opts: CreateApiClientOptions): ApiClient {
  const doFetch = opts.fetchImpl ?? fetch;
  const base = opts.baseUrl.replace(/\/$/, '');

  return {
    async get<T>(path: string): Promise<T> {
      let resp: Response;
      try {
        resp = await doFetch(`${base}${path}`, {
          headers: { accept: 'application/json', ...opts.headers },
        });
      } catch (err) {
        throw new ApiError('NETWORK', (err as Error).message ?? 'Network error', 0);
      }
      if (!resp.ok) {
        let payload: { code?: string; message?: string } = {};
        try { payload = (await resp.json()) as typeof payload; } catch { /* */ }
        throw new ApiError(payload.code ?? 'HTTP_ERROR', payload.message ?? 'Error', resp.status);
      }
      return (await resp.json()) as T;
    },
  };
}
